import { economyConfig } from '../config/economyConfig';
import { getDistrict, type District } from '../data/districts';
import type { GameTime } from '../models/GameTime';
import type { ParkingLot } from '../models/ParkingLot';
import { clamp } from '../utils/math';
import type { RandomGenerator } from '../utils/random';
import { UpgradeEngine } from './UpgradeEngine';

/**
 * DemandEngine (spec section 11): computes how many cars want to enter a lot.
 *
 * DemandScore (cars/hour) =
 *   BaseDemand × TimeMultiplier × DayMultiplier × PriceMultiplier
 *             × ReputationMultiplier × GlobalMultiplier
 *
 * Competition (Phase 9), events (Phase 10) and availability are not yet folded
 * in; they multiply in here later. Pure & deterministic (rng only used to turn
 * a fractional expected arrival count into a whole number).
 */
export const DemandEngine = {
  /** Weekend = Saturday (5) or Sunday (6); GameClock uses Monday = 0. */
  isWeekend(time: GameTime): boolean {
    return time.dayOfWeek === 5 || time.dayOfWeek === 6;
  },

  isPeakHour(district: District, hour: number): boolean {
    return district.peakHours.some(([start, end]) => hour >= start && hour < end);
  },

  timeMultiplier(district: District, time: GameTime): number {
    return this.timeMultiplierAtHour(district, time.hour);
  },

  timeMultiplierAtHour(district: District, hour: number): number {
    const { DEMAND } = economyConfig;
    if (this.isPeakHour(district, hour)) return DEMAND.TIME_PEAK;
    if (hour >= 6 && hour < 22) return DEMAND.TIME_DAY;
    return DEMAND.TIME_NIGHT;
  },

  dayMultiplier(district: District, time: GameTime): number {
    return this.isWeekend(time)
      ? district.weekendMultiplier
      : district.weekdayMultiplier;
  },

  /**
   * Price sensitivity (spec section 12). At the market price the modifier is
   * 1.0; higher prices reduce demand, lower prices raise it, clamped.
   */
  priceMultiplier(price: number): number {
    const { AVERAGE_MARKET_PRICE, DEMAND } = economyConfig;
    const rel = (price - AVERAGE_MARKET_PRICE) / AVERAGE_MARKET_PRICE;
    const mod = 1 - rel * DEMAND.PRICE_SLOPE;
    return clamp(mod, DEMAND.PRICE_MOD_MIN, DEMAND.PRICE_MOD_MAX);
  },

  reputationMultiplier(reputation: number): number {
    const { DEMAND } = economyConfig;
    return DEMAND.REP_MIN + (reputation / 100) * DEMAND.REP_RANGE;
  },

  /**
   * Immediate quality effect on demand from cleanliness + security (driven by
   * staff and CCTV). Unlike reputation (which drifts slowly), this responds the
   * instant you hire staff or buy an upgrade, so the impact is visible.
   * Range ~0.8 (filthy/unsafe) to ~1.2 (spotless/secure).
   */
  qualityMultiplier(lot: ParkingLot): number {
    const clean = clamp(lot.cleanliness, 0, 100);
    const security = clamp(lot.security, 0, 100);
    return 0.8 + ((clean + security) / 200) * 0.4;
  },

  /** Expected cars per hour for a lot at the given time. */
  computeDemandScore(lot: ParkingLot, time: GameTime): number {
    const district = getDistrict(lot.districtId);
    if (!district) return 0;

    // Upgrades like EV chargers / smart guidance add a demand bonus.
    const upgradeMultiplier = UpgradeEngine.aggregate(lot).demandMultiplier;

    return (
      district.baseDemand *
      this.timeMultiplier(district, time) *
      this.dayMultiplier(district, time) *
      this.priceMultiplier(lot.parkingPrice) *
      this.reputationMultiplier(lot.reputation) *
      this.qualityMultiplier(lot) *
      upgradeMultiplier *
      economyConfig.DEMAND_MULTIPLIER
    );
  },

  /**
   * Expected arrivals over `dtMinutes` (a fractional number). `competitionMult`
   * (0..1) is the fraction of local demand this lot captures vs competitors in
   * the same district (1 when it has none) — computed by MarketEngine.
   */
  expectedArrivals(
    lot: ParkingLot,
    time: GameTime,
    dtMinutes: number,
    competitionMult = 1
  ): number {
    return (this.computeDemandScore(lot, time) * competitionMult * dtMinutes) / 60;
  },

  /**
   * Turn a fractional expected count into a whole number of spawns this tick:
   * the integer part always, plus the fractional part with matching probability.
   */
  sampleArrivals(expected: number, rng: RandomGenerator): number {
    if (expected <= 0) return 0;
    const whole = Math.floor(expected);
    const frac = expected - whole;
    return whole + (rng.next() < frac ? 1 : 0);
  },
};
